import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDownload,
  FiAlertCircle,
  FiShield,
  FiUserCheck
} from 'react-icons/fi';
import {
  FaIdCard,
  FaRegIdCard,
  FaRegFilePdf,
  FaRegImage,
  FaCheck,
  FaTimes,
  FaSpinner
} from 'react-icons/fa';

const KYCApprovalRequest = () => {
  const [kycRequests, setKycRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatStatus = (status) => {
    switch(status) {
      case 'Pending': return { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: <FiClock className="w-3 h-3" /> };
      case 'Approved': return { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: <FiCheckCircle className="w-3 h-3" /> };
      case 'Rejected': return { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: <FiXCircle className="w-3 h-3" /> };
      default: return { label: 'Unknown', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: <FiAlertCircle className="w-3 h-3" /> };
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const openDeleteModal = (request) => {
    setRequestToDelete(request);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRequestToDelete(null);
  };

  const handleApproveReject = async (status, remarks = '') => {
    setActionLoading(true);
    try {
      const response = await sendApiRequest(
        `api/kyc/update-approval`,
        { 
          kycId: selectedRequest._id,
          status,
          remarks: status === 'Rejected' ? (remarks || 'Please check your documents and resubmit') : 'All documents verified successfully'
        },
        "PUT"
      );
      
      if (response.data.success) {
        toast.success(`KYC request ${status.toLowerCase()} successfully`);
        setKycRequests(kycRequests.map(request => 
          request._id === selectedRequest._id ? { ...request, status } : request
        ));
        closeModal();
        fetchAllKYCRequests();
      } else {
        toast.error(response.data.message || `Failed to ${status.toLowerCase()} KYC request`);
      }
    } catch (error) {
      console.error('Error updating KYC status:', error);
      toast.error('An error occurred while updating KYC status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    setActionLoading(true);
    try {
      const response = await sendApiRequest(
        `api/kyc/delete/${requestToDelete._id}`,
        {},
        "DELETE"
      );
      
      if (response.data.success) {
        toast.success('KYC request deleted successfully');
        setKycRequests(kycRequests.filter(request => request._id !== requestToDelete._id));
        closeDeleteModal();
        fetchAllKYCRequests();
      } else {
        toast.error(response.data.message || 'Failed to delete KYC request');
      }
    } catch (error) {
      console.error('Error deleting KYC request:', error);
      toast.error('An error occurred while deleting KYC request');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchAllKYCRequests = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/kyc/all-history', {}, "GET");
      if (response.data.success) {
        setKycRequests(response.data.data);
        setFilteredRequests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
      toast.error('Failed to fetch KYC requests');
    } finally {
      setLoading(false);
    }
  };

  // Filter KYC requests based on search term and status
  useEffect(() => {
    let filtered = kycRequests;
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(request => 
        request.userDetails?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.userDetails?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.userDetails?.phone?.includes(searchTerm) ||
        request.panNo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  }, [searchTerm, statusFilter, kycRequests]);

  useEffect(() => {
    fetchAllKYCRequests();
  }, []);

  const stats = {
    total: kycRequests.length,
    pending: kycRequests.filter(r => r.status === 'Pending').length,
    approved: kycRequests.filter(r => r.status === 'Approved').length,
    rejected: kycRequests.filter(r => r.status === 'Rejected').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  KYC Approval Requests
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Review and manage customer verification requests</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchAllKYCRequests}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Requests</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                <p className="text-xs text-gray-400 mt-1">All submissions</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FiFileText className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
                <p className="text-xs text-gray-400 mt-1">Awaiting review</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <FiClock className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.approved}</p>
                <p className="text-xs text-gray-400 mt-1">Verified</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FiCheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
                <p className="text-xs text-gray-400 mt-1">Not verified</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg">
                <FiXCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone or PAN..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {filteredRequests.length} of {kycRequests.length} requests
          </p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">PAN / Aadhaar</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Submitted On</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {loading ? (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="relative">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <FiShield className="w-5 h-5 text-indigo-400 animate-pulse" />
                            </div>
                          </div>
                          <p className="mt-4 text-gray-500 font-medium">Loading KYC requests...</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : filteredRequests.length === 0 ? (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td colSpan="6" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiFileText className="w-10 h-10 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium text-lg">No KYC requests found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    filteredRequests.map((request, index) => {
                      const status = formatStatus(request.status);
                      return (
                        <motion.tr
                          key={request._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                                <FiUser className="w-5 h-5 text-indigo-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{request.userDetails?.fullName || 'User Deleted'}</p>
                                <p className="text-xs text-gray-500">{request.userDetails?.email || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-gray-600">
                              <FiPhone className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{request.userDetails?.phone || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <p className="text-sm font-mono text-gray-700">{request.panNo}</p>
                              <p className="text-sm font-mono text-gray-500">{request.aadhaarNo}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(request.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${status.color}`}>
                              {status.icon}
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => openModal(request)}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                                title="View Details"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(request)}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition"
                                title="Delete Request"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FiShield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">KYC Verification Details</h2>
                      <p className="text-indigo-100 text-sm">{selectedRequest.userDetails?.fullName || 'User Deleted'}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white hover:bg-white/20 rounded-lg p-1 transition">
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User Information */}
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiUser className="w-4 h-4 text-indigo-500" />
                      User Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500">Full Name</p>
                        <p className="font-medium text-gray-800">{selectedRequest.userDetails?.fullName || 'User Deleted'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email Address</p>
                        <p className="text-gray-700">{selectedRequest.userDetails?.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Phone Number</p>
                        <p className="text-gray-700">{selectedRequest.userDetails?.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">User ID</p>
                        <p className="font-mono text-sm text-indigo-600">{selectedRequest.userDetails?.sponsorCode || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* KYC Information */}
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FaRegIdCard className="w-4 h-4 text-indigo-500" />
                      KYC Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500">PAN Number</p>
                        <p className="font-mono text-gray-700">{selectedRequest.panNo}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Aadhaar Number</p>
                        <p className="font-mono text-gray-700">{selectedRequest.aadhaarNo}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${formatStatus(selectedRequest.status).color}`}>
                          {formatStatus(selectedRequest.status).icon}
                          {formatStatus(selectedRequest.status).label}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Submitted On</p>
                        <p className="text-gray-700">{formatDate(selectedRequest.createdAt)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Document Links */}
                  <div className="md:col-span-2 bg-gray-50 rounded-xl p-5 border border-gray-200">
                    <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FiFileText className="w-4 h-4 text-indigo-500" />
                      Document Links
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <a 
                        href={selectedRequest.panScanCopy} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition"
                      >
                        <FaRegImage className="w-5 h-5 text-indigo-500" />
                        <span className="text-sm font-medium text-gray-700">PAN Document</span>
                      </a>
                      <a 
                        href={selectedRequest.aadhaarScanCopy} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition"
                      >
                        <FaRegImage className="w-5 h-5 text-indigo-500" />
                        <span className="text-sm font-medium text-gray-700">Aadhaar Document</span>
                      </a>
                      <a 
                        href={selectedRequest.cancelChequeCopy} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition"
                      >
                        <FaRegImage className="w-5 h-5 text-indigo-500" />
                        <span className="text-sm font-medium text-gray-700">Cancel Cheque</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedRequest.status === 'Pending' && (
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApproveReject('Rejected')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-6 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition disabled:opacity-50"
                    >
                      {actionLoading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FiXCircle className="w-4 h-4" />}
                      Reject
                    </button>
                    <button
                      onClick={() => handleApproveReject('Approved')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-6 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition disabled:opacity-50"
                    >
                      {actionLoading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
                      Approve
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && requestToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeDeleteModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-red-500 to-rose-500 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <FiTrash2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-semibold text-lg">Confirm Deletion</h2>
                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete the KYC request for <strong>{requestToDelete.userDetails?.fullName || 'this user'}</strong>?
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={closeDeleteModal}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteRequest}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition disabled:opacity-50"
                  >
                    {actionLoading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KYCApprovalRequest;